import { Socket } from 'socket.io-client';
import { CreateGameConfig, Game, Teams } from '@par/shared/types/game';
import {
  ChatMessage,
  GameDataFromServer,
  Maintenance,
  Mode,
  Player,
  WhosOnline,
} from '../components/room/types';

export const socketGlobalListeners = (
  changeGameMode: (mode: Mode) => void,
  handleReadyCancelByOtherPlayer: (username: string) => void,
  lobbyGameUpdate: (update: {
    gameId: string;
    game: Game;
    action: 'create' | 'update' | 'delete';
  }) => void,
  lobbyGamePropertyUpdate: (update: {
    gameId: string;
    property: keyof Game;
    data: any;
  }) => void,
  lobbyPlayerUpdate: (update: {
    gameId: string;
    player: any;
    action: 'addPlayer';
  }) => void,
  profileId: string,
  promptReadyCheck: (data: {
    mode: Mode;
    players: Record<string, Player>;
    bannedCategories: string[];
    confirmReadyExpiresAt?: number;
    autoStartIn?: number;
  }) => void,
  setAllPlayersReady: (ready: boolean) => void,
  reverseAllChat: (allChat: ChatMessage[]) => void,
  setAllLobbyGames: (lobby: Game[]) => void,
  setMaintenance: (opts: Maintenance) => void,
  setBannerMessage: (bannerMessage: string) => void,
  setLatestClientVersion: (clientVersion: number) => void,
  updatePlayerIsReady: (player: Player) => void,
  setGameDataFromServer: (gameDataFromServer: GameDataFromServer) => void,
  setOnlinePlayer: (
    onlinePlayer: WhosOnline & { currentGameInfo?: CreateGameConfig }
  ) => void,
  setWhosOnline: (playersOnline: WhosOnline[]) => void,
  setPlayersOnlineCount: (count: number) => void,
  setTeamPickingStatus: (data: {
    host: string;
    teams: Teams;
    players: Record<string, Player>;
  }) => void,
  setEnabledTeams: (enabled: boolean) => void,
  updateWhosOnline: (loggedInUser: WhosOnline) => void,
  socket: Socket
) => {
  socket.on('whosOnline', setWhosOnline);
  socket.on('playersOnlineCount', setPlayersOnlineCount);
  socket.on('onlinePlayer', setOnlinePlayer);
  socket.on('confirmReady', promptReadyCheck);
  socket.on('allChat', reverseAllChat);
  socket.on('gameDataFromServer', setGameDataFromServer);
  socket.on('lobby', setAllLobbyGames);
  socket.on('lobbyGameUpdate', lobbyGameUpdate);
  socket.on('lobbyGamePropertyUpdate', lobbyGamePropertyUpdate);
  socket.on('lobbyPlayerUpdate', lobbyPlayerUpdate);
  socket.on('playerIsReady', updatePlayerIsReady);
  socket.on('playerIsNotReady', handleReadyCancelByOtherPlayer);
  socket.on('allPlayersReady', setAllPlayersReady);
  socket.on('maintenance', setMaintenance);
  socket.on('bannerMessage', setBannerMessage);
  socket.on('latestClientVersion', setLatestClientVersion);
  socket.on('marco', () => {
    socket.emit('polo', { profileId });
  });
  socket.on('changeGameMode', changeGameMode);
  socket.on('teamPickingStatus', setTeamPickingStatus);
  socket.on('enabledTeams', setEnabledTeams);
  socket.on('whosOnlineUpdate', updateWhosOnline);
};

export const socketRemoveGlobalListeners = (socket: Socket) => {
  socket.off('disconnect');
  socket.off('whosOnline');
  socket.off('whosOnlineUpdate');
  socket.off('playersOnlineCount');
  socket.off('onlinePlayer');
  socket.off('confirmReady');
  socket.off('allChat');
  socket.off('gameDataFromServer');
  socket.off('lobby');
  socket.off('lobbyGameUpdate');
  socket.off('lobbyPlayerUpdate');
  socket.off('playerIsReady');
  socket.off('playerIsNotReady');
  socket.off('allPlayersReady');
  socket.off('maintenance');
  socket.off('bannerMessage');
  socket.off('latestClientVersion');
  socket.off('marco');
};

// this is almost NEVER needed because socket listeners stay alive even on disconnects/reconnects
export const cleanUpRoomListeners = (socket: Socket) => {
  socket.off('disconnect');
  socket.off('players');
  socket.off('playerUpdate');
  socket.off('detailedPlayers');
  socket.off('gameStarted');
  socket.off('countdown');
  socket.off('questionIndex');
  socket.off('questionMetadata');
  socket.off('gameClock');
  socket.off('revealAnswer');
  socket.off('revealQuestion');
  socket.off('revealFullQuestion');
  socket.off('timerLimitReached');
  socket.off('unrevealedIndex');
  socket.off('gameOver');
  socket.off('roundOver');
  socket.off('questionSet');
  socket.off('properNameShortAnswer');
  socket.off('reportInaccuracy');
  socket.off('bannedCategories');
  socket.off('delayBeforeQuestionRevealCount');
  socket.off('nextQuestionDelayCount');
  socket.off('nextRoundStartTime');
  socket.off('averageDifficulty');
  socket.off('fuzzy');
  socket.off('paused');
  socket.off('willPause');
  socket.off('unpauseCount');
  socket.off('cutoff');
  socket.off('spectateData');
  socket.off('spectators');
  socket.off('handicap');
  socket.off('penalty');
  socket.off('rejoinedData');
  socket.off('survivalChallengeTimerLimit');
  socket.off('survivalChallengePhaseTwoChallenge');
  socket.off('survivalChallengeLivesRemaining');
};
